import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';

import { authService } from '../../services/api';
import AuthShell from './AuthShell';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (formData.password.length < 8) {
      setError('Use at least 8 characters.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await authService.confirmPasswordReset(token, formData.password);
      setComplete(true);
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'We could not reset your password. Request a new link and try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Account security"
      title={complete ? 'Password updated' : 'Choose a new password'}
      description={
        complete
          ? 'Your Timelyzer account is ready. Sign in with your new password to continue.'
          : 'Create a new password for your account. This reset link can only be used once.'
      }
    >
      {complete ? (
        <div>
          <div className="border-y border-[#dce3dd] py-6">
            <div className="flex items-start gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#deede5] text-[#246b52]">
                <CheckCircleIcon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-bold text-[#26342c]">Your password has been reset</p>
                <p className="mt-2 text-sm leading-6 text-[#65716a]">
                  The recovery link is no longer valid and cannot be used again.
                </p>
              </div>
            </div>
          </div>
          <Link to="/login" className="primary-button mt-6 w-full">
            Sign in
            <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </div>
      ) : !token ? (
        <div>
          <div role="alert" className="border-y border-[#e5c7be] bg-[#fbf2ef] px-4 py-5">
            <p className="text-sm font-bold text-[#783b2b]">This reset link is incomplete</p>
            <p className="mt-2 text-sm leading-6 text-[#934632]">
              Request a new password reset link to continue.
            </p>
          </div>
          <Link to="/forgot-password" className="primary-button mt-6 w-full">
            Request a new link
            <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </div>
      ) : (
        <>
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="password" className="field-label">New password</label>
              <div className="relative">
                <KeyIcon className="pointer-events-none absolute left-3.5 top-3.5 h-4 w-4 text-[#7c8780]" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={formData.password}
                  onChange={handleChange}
                  className="field-input pl-10 pr-12"
                  placeholder="At least 8 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  className="absolute right-1 top-1 flex h-10 w-10 items-center justify-center rounded-md text-[#6d7871] hover:bg-[#edf1ed]"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
              <p className="mt-2 text-xs leading-5 text-[#7a857e]">Use at least 8 characters and avoid a password used elsewhere.</p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="field-label">Confirm new password</label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="field-input"
                placeholder="Repeat your new password"
              />
            </div>

            {error && (
              <p role="alert" className="rounded-md border border-[#e5c7be] bg-[#fbf2ef] px-3 py-2 text-sm text-[#934632]">
                {error}
              </p>
            )}

            <button type="submit" disabled={loading} className="primary-button w-full disabled:cursor-not-allowed disabled:opacity-60">
              {loading ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Updating password
                </>
              ) : (
                <>
                  Update password
                  <ArrowRightIcon className="h-4 w-4" />
                </>
              )}
            </button>
          </form>

          <div className="mt-7 border-t border-[#dce3dd] pt-6 text-center">
            <Link to="/login" className="inline-flex items-center gap-2 text-sm font-bold text-[#246b52] hover:text-[#18513d]">
              <ArrowLeftIcon className="h-4 w-4" />
              Back to sign in
            </Link>
          </div>
        </>
      )}
    </AuthShell>
  );
};

const getErrorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg.replace(/^Value error, /, '');
  return fallback;
};

export default ResetPassword;
